import { type ReactElement, useCallback, useMemo, useState } from 'react';
import { NavigateFunction, useLocation, useNavigate } from 'react-router-dom';
import track, { errorTypes } from 'c9-tracking';
import { useSnackbar } from 'notistack';

import { type TAppContext, useAppContext } from 'context/AppContext';
import { type TConfig, type TEndpointAPIConfig, useConfigContext } from 'context/ConfigContext';

import { updateGiftCard } from 'common/async/async-calls';
import { getProductValue } from 'common/helpers/product';

import GiftCardForm, { TFormData } from '../GiftCardForm/GiftCardForm';

/* ============================================ Local Types ============================================ */
type TUseFormData = (
  locationState: TFormData & { id?: string },
  updateGiftCardEndpoint: TEndpointAPIConfig['updateGiftCard'],
  appBasePath: TConfig['appBasePath'],
  userEmail: TAppContext['user']['userDetails']['email'],
  navigate: NavigateFunction,
  enqueueSnackbar: (message: string) => void
) => {
  isLoading: boolean;
  initialFormData: TFormData;
  errorMessage: string;
  onSubmit: (data: Required<TFormData>) => void;
  onCancel: () => void;
};

type TUpdateGiftCardPage = () => ReactElement;

/* ============================================ Local Functions ============================================ */
const useFormData: TUseFormData = (
  locationState,
  updateGiftCardEndpoint,
  appBasePath,
  userEmail,
  navigate,
  enqueueSnackbar
) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const initialFormData: TFormData = useMemo(
    () => ({
      type: 'update',
      amount: locationState.amount,
      product: locationState.product,
      startDate: locationState.startDate,
      endDate: locationState.endDate,
      campaign: locationState.campaign
    }),
    [locationState]
  );

  const handleOnSubmit: ReturnType<TUseFormData>['onSubmit'] = useCallback(
    (data) => {
      setIsLoading(true);
      setErrorMessage('');

      updateGiftCard(updateGiftCardEndpoint, locationState.id ?? '', {
        key: getProductValue(data.product),
        amount: data.amount,
        startDate: data.startDate,
        endDate: data.endDate,
        campaign: data.campaign
      })
        .then(() => {
          track.interact(userEmail + ' updated ' + getProductValue(data.product) + ' giftcard');
          track.form('add-giftcard', [
            { id: 'user', value: userEmail },
            { id: 'key', value: getProductValue(data.product) },
            { id: 'amount', value: data.amount },
            { id: 'startDate', value: data.startDate.toString() },
            { id: 'endDate', value: data.endDate.toString() },
            { id: 'campaign', value: data.campaign }
          ]);
          enqueueSnackbar(`Gift card updated`);
          window.location.href = appBasePath;
        })
        .catch((error: Error) => {
          setErrorMessage(error.message);
          track.fault('Failed to update giftcard', errorTypes.tech, error.message);
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [locationState.id, updateGiftCardEndpoint, appBasePath, userEmail, enqueueSnackbar]
  );

  const handleOnCancel = useCallback(() => {
    setErrorMessage('');
    navigate('/');
  }, [navigate]);

  return { isLoading, initialFormData, errorMessage, onSubmit: handleOnSubmit, onCancel: handleOnCancel };
};

/* ============================================ Main Component ============================================ */
const UpdateGiftCardPage: TUpdateGiftCardPage = () => {
  const { state: locationState } = useLocation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const {
    user: {
      userDetails: { email: userEmail }
    }
  } = useAppContext();
  const {
    appBasePath,
    endpointAPI: { updateGiftCard: updateGiftCardEndpoint }
  } = useConfigContext();

  const { isLoading, initialFormData, errorMessage, onSubmit, onCancel } = useFormData(
    locationState,
    updateGiftCardEndpoint,
    appBasePath,
    userEmail,
    navigate,
    enqueueSnackbar
  );

  return (
    <GiftCardForm
      title="Update Gift Card"
      isLoading={isLoading}
      errorMessage={errorMessage}
      initialFormData={initialFormData}
      onSubmit={onSubmit}
      onCancel={onCancel}
    />
  );
};

export default UpdateGiftCardPage;
